/**
 * Universal Virtual File Store (VFS) abstraction.
 * Enforces authenticated AES-GCM encryption-at-rest with PBKDF2 key derivation.
 *
 * Dispatches asynchronously to:
 * - Browser: Origin Private File System (OPFS) via navigator.storage.getDirectory()
 * - Server: Sandboxed file operations via node:fs/promises
 * - Fallback: In-memory virtual filesystem for restricted sandboxes
 */
import { CONTEXT, getContext } from '#library/utility.library.js';
import { objectify, stringify } from '#library/serialize.library.js';
import { encryptWithPassword, decryptWithPassword } from '#library/cipher.library.js';
import { Logger } from '#library/logger.class.js';
const log = new Logger('[FileStore]');
const context = getContext();
// In-Memory Fallback File Map for restricted environments
const memoryVFS = new Map();
const memoryRawProvider = {
    async readBuffer(path) {
        return memoryVFS.get(path) ?? null;
    },
    async write(path, content) {
        memoryVFS.set(path, content);
    },
    async delete(path) {
        return memoryVFS.delete(path);
    },
    async exists(path) {
        return memoryVFS.has(path);
    },
    async list(dir = '') {
        const prefix = dir ? (dir.endsWith('/') ? dir : dir + '/') : '';
        const entries = new Set();
        for (const key of memoryVFS.keys()) {
            if (!prefix || key.startsWith(prefix)) {
                const rest = key.slice(prefix.length);
                if (rest.length > 0) {
                    const segment = rest.split('/')[0];
                    if (segment)
                        entries.add(segment);
                }
            }
        }
        return Array.from(entries);
    }
};
let rawProviderPromise = null;
async function getRawProvider() {
    if (!rawProviderPromise) {
        rawProviderPromise = (async () => {
            if (context.type === CONTEXT.Browser || context.type === CONTEXT.WebWorker) {
                const { isOPFSSupported, opfsReadBuffer, opfsWrite, opfsDelete, opfsExists, opfsList } = await import('#browser/opfs.library.js');
                if (isOPFSSupported()) {
                    return {
                        readBuffer: opfsReadBuffer,
                        write: (p, c) => opfsWrite(p, c),
                        delete: opfsDelete,
                        exists: opfsExists,
                        list: opfsList
                    };
                }
            }
            else if (context.type === CONTEXT.NodeJS) {
                const { serverReadBuffer, serverWrite, serverDelete, serverExists, serverList } = await import('#server/filestore.library.js');
                return {
                    readBuffer: (p) => serverReadBuffer(p),
                    write: (p, c) => serverWrite(p, c),
                    delete: (p) => serverDelete(p),
                    exists: (p) => serverExists(p),
                    list: (d) => serverList(d)
                };
            }
            return memoryRawProvider;
        })().catch(err => {
            rawProviderPromise = null;
            throw err;
        });
    }
    return rawProviderPromise;
}
function resolveNamespacedPath(path, namespace) {
    if (!namespace)
        return path;
    const cleanNs = namespace.replace(/^\/+|\/+$/g, '');
    const cleanPath = path.replace(/^\/+/, '');
    return `${cleanNs}/${cleanPath}`;
}
/**
 * Creates or retrieves an encrypted Virtual File Store.
 * A non-empty password is required. All data stored is encrypted at-rest using AES-GCM (256-bit).
 *
 * @param options - Encryption password string or FileStoreOptions object
 * @returns Encrypted VirtualFileStore instance
 */
export async function getFileStore(options) {
    const config = typeof options === 'string' ? { password: options } : options;
    if (!config || !config.password || typeof config.password !== 'string' || config.password.trim() === '') {
        throw new Error('FileStore: A non-empty password is required. All virtual file stores must be encrypted at-rest.');
    }
    const { password, namespace, iterations = 100_000 } = config;
    const provider = await getRawProvider();
    const store = {
        async read(path) {
            const target = resolveNamespacedPath(path, namespace);
            const raw = await provider.readBuffer(target);
            if (raw === null)
                return null;
            const decrypted = await decryptWithPassword(raw, password, iterations);
            return new TextDecoder().decode(decrypted);
        },
        async readBuffer(path) {
            const target = resolveNamespacedPath(path, namespace);
            const raw = await provider.readBuffer(target);
            if (raw === null)
                return null;
            return decryptWithPassword(raw, password, iterations);
        },
        async write(path, content) {
            const target = resolveNamespacedPath(path, namespace);
            const encrypted = await encryptWithPassword(content, password, iterations);
            await provider.write(target, encrypted);
        },
        async delete(path) {
            const target = resolveNamespacedPath(path, namespace);
            return provider.delete(target);
        },
        async exists(path) {
            const target = resolveNamespacedPath(path, namespace);
            return provider.exists(target);
        },
        async list(dir = '') {
            const target = resolveNamespacedPath(dir, namespace);
            return provider.list(target);
        },
        async readJSON(path, fallback) {
            const raw = await store.read(path);
            if (raw === null)
                return fallback ?? null;
            try {
                const parsed = objectify(raw);
                return parsed !== undefined ? parsed : (fallback ?? null);
            }
            catch {
                return fallback ?? null;
            }
        },
        async writeJSON(path, data) {
            await store.write(path, stringify(data));
        }
    };
    return store;
}
/**
 * Convenience alias for getFileStore.
 */
export const createEncryptedFileStore = getFileStore;
/**
 * Reads an encrypted text file from the default Virtual File Store.
 *
 * @param path - Virtual relative path
 * @param password - Decryption password
 */
export async function fileRead(path, password) {
    const store = await getFileStore(password);
    return store.read(path);
}
/**
 * Reads an encrypted binary file as a Uint8Array from the default Virtual File Store.
 *
 * @param path - Virtual relative path
 * @param password - Decryption password
 */
export async function fileReadBuffer(path, password) {
    const store = await getFileStore(password);
    return store.readBuffer(path);
}
/**
 * Encrypts and writes data to the default Virtual File Store.
 *
 * @param path - Virtual relative path
 * @param content - String or binary payload
 * @param password - Encryption password
 */
export async function fileWrite(path, content, password) {
    const store = await getFileStore(password);
    return store.write(path, content);
}
/**
 * Deletes a file from the default Virtual File Store.
 *
 * @param path - Virtual relative path
 */
export async function fileDelete(path) {
    const provider = await getRawProvider();
    return provider.delete(path);
}
/**
 * Checks whether a file exists in the default Virtual File Store.
 *
 * @param path - Virtual relative path
 */
export async function fileExists(path) {
    const provider = await getRawProvider();
    return provider.exists(path);
}
/**
 * Lists entry names within a virtual directory.
 *
 * @param dir - Optional subdirectory
 */
export async function fileList(dir = '') {
    const provider = await getRawProvider();
    return provider.list(dir);
}
/**
 * Reads and decrypts a JSON file from the default Virtual File Store.
 *
 * @param path - Virtual relative path
 * @param password - Decryption password
 * @param fallback - Optional fallback value
 */
export async function fileReadJSON(path, password, fallback) {
    const store = await getFileStore(password);
    return store.readJSON(path, fallback);
}
/**
 * Serializes, encrypts, and writes data to a JSON file in the default Virtual File Store.
 *
 * @param path - Virtual relative path
 * @param data - Object or value to store
 * @param password - Encryption password
 */
export async function fileWriteJSON(path, data, password) {
    const store = await getFileStore(password);
    return store.writeJSON(path, data);
}
